import React, { useEffect, useMemo, useState } from "react";
import { useToast } from "../../context/ToastContext";
import { apiClient } from "../../utils/http";
import { EVENT_TYPES, EVENT_TYPE_META, MONTH_FULL_NAMES, WEEKDAY_NAMES } from "../../utils/constants";
import { todayISO } from "../../utils/format";
import { alertMessage, alertTitle, digestText, openWhatsApp, reminderText } from "../../utils/whatsapp";
import ScheduleFeedList from "./ScheduleFeedList";
import useScheduleFeed from "./useScheduleFeed";
import CalendarIcon from "./CalendarIcon";
import WhatsAppIcon from "./WhatsAppIcon";

/* The 6-week (42-cell) grid for a month, starting on the Sunday on or before the 1st. */
const monthCells = (year, month) => {
  const lead = new Date(year, month, 1).getDay();
  return Array.from({ length: 42 }, (_, i) => {
    const date = new Date(year, month, 1 - lead + i);
    return { iso: todayISO(date), day: date.getDate(), inMonth: date.getMonth() === month };
  });
};

const STAT_CARDS = [
  { key: "upcoming_jobs", className: "stat-upcoming-jobs", label: "Upcoming Jobs" },
  { key: "meetings", className: "stat-meetings", label: "Scheduled Client Meetings" },
  { key: "pending_tasks", className: "stat-tasks", label: "Pending Tasks" },
  { key: "active_reminders", className: "stat-reminders", label: "Active Date Reminders" },
];

/* Calendar & Work Schedule Dashboard: stats, month grid, reminder alerts and the upcoming list. */
export default function CalendarModal({ schedule, canAdd }) {
  const showToast = useToast();
  const { summary, version, selectedDate, openDay, openEventForm, closeCalendar } = schedule;
  const today = todayISO();
  // opened from a widget item: start on that item's month
  const [view, setView] = useState(() => {
    const [year, month] = (selectedDate || today).split("-").map(Number);
    return { year, month: month - 1 };
  });
  const [monthItems, setMonthItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const upcoming = useScheduleFeed({ type: filter === "all" ? null : filter, version, showToast });

  const cells = useMemo(() => monthCells(view.year, view.month), [view]);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get("schedule", { params: { from: cells[0].iso, to: cells[cells.length - 1].iso } })
      .then((res) => !cancelled && setMonthItems(res.data.items))
      .catch((err) => !cancelled && showToast(err.message, "error"));
    return () => {
      cancelled = true;
    };
  }, [cells, version, showToast]);

  const byDate = useMemo(() => {
    const map = {};
    monthItems.forEach((item) => {
      (map[item.date] = map[item.date] || []).push(item);
    });
    return map;
  }, [monthItems]);

  const shiftMonth = (delta) => {
    setView(({ year, month }) => {
      const date = new Date(year, month + delta, 1);
      return { year: date.getFullYear(), month: date.getMonth() };
    });
  };

  const alerts = summary ? summary.alerts : [];
  const stats = summary ? summary.stats : {};

  const sendAlert = (item) => {
    openWhatsApp(reminderText({ ...item, title: alertTitle(item) }), item.source === "job" ? item.mobile : "");
    showToast("Opening WhatsApp with reminder alert...", "info");
  };

  const sendAllAlerts = () => {
    if (alerts.length === 0) {
      showToast("No active reminders for today or tomorrow to send via WhatsApp.", "info");
      return;
    }
    openWhatsApp(digestText(alerts));
    showToast("Opening WhatsApp with daily reminders summary...", "info");
  };

  const upcomingCount = upcoming.items ? upcoming.total : 0;

  return (
    <div className="modal-backdrop no-print">
      <div className="calendar-modal-container">
        <div className="calendar-header">
          <div className="header-left-title">
            <CalendarIcon className="red-icon" size={24} />
            <h2>Calendar &amp; Work Schedule Dashboard</h2>
          </div>
          <div className="calendar-header-actions">
            {canAdd && (
              <button type="button" className="btn btn-primary btn-sm" onClick={() => openEventForm("job")}>
                <svg viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" /></svg>
                New Booking / Event
              </button>
            )}
            <button type="button" className="btn-icon-close" title="Close Calendar" onClick={closeCalendar}>
              &times;
            </button>
          </div>
        </div>

        <div className="calendar-modal-body">
          <div className="schedule-stats-row">
            {STAT_CARDS.map((card) => (
              <div key={card.key} className={`schedule-stat-card ${card.className}`}>
                <span className="stat-num">{stats[card.key] ?? 0}</span>
                <span className="stat-lbl">{card.label}</span>
              </div>
            ))}
          </div>

          <div className="calendar-dashboard-grid">
            <div className="calendar-left-pane">
              <div className="calendar-widget-card">
                <div className="calendar-month-nav">
                  <button type="button" className="btn-icon-sm" title="Previous Month" onClick={() => shiftMonth(-1)}>
                    &lt;
                  </button>
                  <h3>
                    {MONTH_FULL_NAMES[view.month]} {view.year}
                  </h3>
                  <button type="button" className="btn-icon-sm" title="Next Month" onClick={() => shiftMonth(1)}>
                    &gt;
                  </button>
                </div>

                <div className="calendar-legend-row">
                  <span className="legend-dot job-dot"></span> Work Job
                  <span className="legend-dot meeting-dot"></span> Client Meeting
                  <span className="legend-dot task-dot"></span> Task
                </div>

                <div className="calendar-days-header">
                  {WEEKDAY_NAMES.map((name) => (
                    <span key={name}>{name}</span>
                  ))}
                </div>
                <div className="calendar-date-grid full-grid">
                  {cells.map((cell) => {
                    const items = byDate[cell.iso] || [];
                    const has = (type) => items.some((item) => item.type === type);
                    const classes = ["cal-day-cell"];
                    if (!cell.inMonth) classes.push("other-month");
                    if (cell.iso === today) classes.push("today");
                    if (cell.iso === selectedDate) classes.push("selected");
                    if (items.length) classes.push("has-marker");
                    if (has("job")) classes.push("has-job-marker");
                    return (
                      <div key={cell.iso} className={classes.join(" ")} role="button" tabIndex={0} onClick={() => openDay(cell.iso)} onKeyDown={(e) => e.key === "Enter" && openDay(cell.iso)}>
                        <span className="day-num">{cell.day}</span>
                        {items.length > 0 && (
                          <>
                            <span className="cal-marker-count" title={`${items.length} item(s) scheduled`}>
                              {items.length}
                            </span>
                            <div className="cal-day-dots">
                              {EVENT_TYPES.filter(has).map((type) => (
                                <span key={type} className={`cal-dot ${type}`} title={EVENT_TYPE_META[type].label}></span>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="reminder-alerts-box mt-3">
                <div className="reminder-box-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>⏰ Date Reminders &amp; Alerts</span>
                  <button type="button" className="btn-whatsapp-sm" title="Send All Alerts Summary via WhatsApp" onClick={sendAllAlerts}>
                    <WhatsAppIcon size={14} />
                    Send All Alerts via WhatsApp
                  </button>
                </div>
                <div className="reminder-alerts-list">
                  {alerts.length === 0 ? (
                    <div className="reminder-item-card info">
                      <span>🔔 No urgent date reminders for today or tomorrow.</span>
                    </div>
                  ) : (
                    alerts.map((item) => (
                      <div key={item.key} className={`reminder-item-card ${item.alert === "today" ? "urgent" : "upcoming"}`}>
                        <div className="reminder-card-content">
                          <span className="reminder-icon">{item.alert === "today" ? "🚨" : "⏰"}</span>
                          <span className="reminder-text">{alertMessage(item)}</span>
                        </div>
                        <button type="button" className="btn-whatsapp-sm" title="Send Alert via WhatsApp" onClick={() => sendAlert(item)}>
                          <WhatsAppIcon size={13} />
                          WhatsApp
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="calendar-right-pane">
              <div className="scheduled-events-sidebar-card">
                <div className="sidebar-events-header">
                  <div className="sidebar-header-title">
                    <h3>📅 Scheduled Events &amp; Content</h3>
                    <span className="events-total-badge">
                      {upcomingCount} Item{upcomingCount === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="sidebar-category-filters">
                    {["all", ...EVENT_TYPES].map((key) => (
                      <button
                        key={key}
                        type="button"
                        className={`btn-sidebar-filter ${filter === key ? "active" : ""}`}
                        onClick={() => setFilter(key)}
                      >
                        {key === "all" ? "All" : EVENT_TYPE_META[key].filter}
                      </button>
                    ))}
                  </div>
                </div>

                <ScheduleFeedList
                  feed={upcoming}
                  className="sidebar-events-scrollable"
                  itemClassName="sidebar-event-item-simple"
                  onOpen={openDay}
                  empty={
                    <div className="schedule-widget-empty" style={{ padding: "2.5rem 1rem" }}>
                      <span>
                        <span className="icon-bright-cal">📅</span> No upcoming {filter === "all" ? "items" : EVENT_TYPE_META[filter].plural} scheduled.
                      </span>
                      {canAdd && (
                        <button type="button" className="btn-widget-calendar" onClick={() => openEventForm(filter === "all" ? "job" : filter)}>
                          + Add New Booking / Event
                        </button>
                      )}
                    </div>
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
